import { useEffect, useRef, useState } from "react";
import { Link, useParams } from "react-router-dom";
import axios from "axios";
import { QRCodeSVG } from "qrcode.react";
import { ArrowLeft, ShieldCheck, ShieldX, Loader2, ArrowRight, ScanSearch, Upload } from "lucide-react";
import { zoomCard } from "@/components/CardZoom";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const RARITY_COLOR = {
  exclusive: "text-red-400 border-red-400/50",
  icon: "text-white border-white/50",
  legendary: "text-violet-300 border-violet-400/50",
  elite: "text-amber-300 border-amber-400/50",
  rare: "text-sky-400 border-sky-500/50",
  common: "text-stone-400 border-stone-500/50",
  gold: "text-yellow-400 border-yellow-500/50",
  holo: "text-fuchsia-300 border-fuchsia-400/50",
  gem: "text-sky-300 border-sky-400/50",
  standard: "text-zinc-400 border-zinc-600",
};

export default function CardVerify() {
  const { serial } = useParams();
  const [data, setData] = useState(null);
  const [scan, setScan] = useState(null);
  const [scanning, setScanning] = useState(false);
  const fileRef = useRef(null);

  const scanImage = (file) => {
    if (!file) return;
    setScanning(true); setScan(null);
    const reader = new FileReader();
    reader.onload = async () => {
      try {
        const { data: r } = await axios.post(`${API}/public/card-verify/scan`, { image: reader.result });
        setScan(r);
      } catch { setScan({ found: false, error: true }); }
      finally { setScanning(false); }
    };
    reader.readAsDataURL(file);
  };

  useEffect(() => {
    axios.get(`${API}/public/card-verify/${serial}`).then(({ data: d }) => setData(d)).catch(() => setData({ registered: false, serial }));
  }, [serial]);

  if (!data) return (
    <div className="min-h-screen bg-[#0A0A0A] flex items-center justify-center">
      <Loader2 className="w-8 h-8 text-lime-400 animate-spin" />
    </div>
  );

  const verifyUrl = `${window.location.origin}/card/verify/${(serial || "").toUpperCase()}`;

  return (
    <div className="min-h-screen bg-[#0A0A0A] text-white px-6 py-10" data-testid="card-verify-page">
      <div className="max-w-2xl mx-auto">
        <Link to="/" data-testid="verify-back-link"
          className="inline-flex items-center gap-1.5 text-xs uppercase tracking-widest text-zinc-500 hover:text-lime-400 transition-colors mb-8">
          <ArrowLeft className="w-3.5 h-3.5" /> Pickle Bullpen
        </Link>
        <p className="font-mono text-lime-300 uppercase tracking-[0.3em] text-xs mb-3">The Card Vault · Official Registry</p>
        <h1 className="font-display font-black uppercase leading-[0.95] text-4xl sm:text-5xl mb-6">Card Verification</h1>

        {!data.registered ? (
          <div className="border border-red-500/50 bg-red-500/10 p-8 text-center" data-testid="verify-not-found">
            <ShieldX className="w-10 h-10 text-red-400 mx-auto mb-3" />
            <div className="font-display font-black text-2xl uppercase text-red-400 mb-2">Not in the registry</div>
            <p className="text-sm text-zinc-400">
              Serial <span className="font-mono font-bold text-white">{data.serial}</span> does not match any card in the
              official Pickle Bullpen registry. This card may be counterfeit or the serial was mistyped.
            </p>
          </div>
        ) : (
          <>
            <div className="border border-emerald-500/50 bg-emerald-500/10 px-5 py-4 flex items-center gap-3 mb-8" data-testid="verify-authentic-banner">
              <ShieldCheck className="w-7 h-7 text-emerald-400 shrink-0" />
              <div>
                <div className="font-display font-black uppercase text-emerald-400 text-lg leading-tight">Authentic — Registered</div>
                <div className="text-xs text-zinc-400">This card is recorded in the official Pickle Bullpen card registry.</div>
              </div>
            </div>

            <div className="grid sm:grid-cols-[220px_1fr] gap-8 mb-10">
              <div className={`border p-2 bg-white/[0.03] ${data.card.one_of_one ? "border-amber-400/70" : "border-zinc-800"}`}>
                <img src={data.card.url} alt={data.card.name} className="w-full aspect-[3/4] object-contain bg-black cursor-zoom-in" data-testid="verify-card-image"
                  title="Tap to zoom" onClick={() => zoomCard(data.card.url, data.card.name)} />
              </div>
              <div className="space-y-4">
                <div>
                  <div className="text-[10px] uppercase tracking-[0.25em] text-zinc-500 mb-1">Card</div>
                  <div className="font-display font-black text-2xl uppercase" data-testid="verify-card-name">{data.card.name}</div>
                  <div className="flex items-center gap-2 mt-1.5 flex-wrap">
                    <span className={`text-[9px] font-black uppercase tracking-widest px-2 py-1 border ${RARITY_COLOR[data.card.rarity] || RARITY_COLOR.standard}`}>
                      {data.card.gem_label || data.card.rarity}
                    </span>
                    {data.card.one_of_one && <span className="text-[9px] font-black uppercase tracking-widest px-2 py-1 border border-amber-400/70 text-amber-400">1 of 1</span>}
                    {data.card.edition && <span className="text-[9px] font-black uppercase tracking-widest px-2 py-1 border border-lime-400/70 text-lime-300" data-testid="verify-edition-badge">No. {data.card.edition}</span>}
                    {data.card.house_copy && <span className="text-[9px] font-black uppercase tracking-widest px-2 py-1 border border-yellow-500 text-yellow-400 bg-yellow-500/10" data-testid="verify-house-copy-badge">★ House Copy — Bullpen Official Reserve</span>}
                    {data.card.autograph && <span className="text-[9px] font-black uppercase tracking-widest px-2 py-1 border border-yellow-400 text-yellow-300 bg-yellow-400/10" data-testid="verify-autograph-badge">🖊 Verified Autograph — {data.card.autograph.pro_name}</span>}
                    {data.card.rc && <span className="text-[9px] font-black uppercase tracking-widest px-2 py-1 bg-red-600 text-white">Rookie Card</span>}
                  </div>
                </div>
                {[["Serial", data.serial, "verify-serial", "font-mono font-black text-lime-300 tracking-widest text-lg"],
                  ["Authenticity Code", data.auth_code, "verify-auth-code", "font-mono font-bold text-zinc-300 tracking-widest"],
                  ["Registered Owner", data.owner_name || "Unassigned", "verify-owner", "font-semibold text-white"],
                  ["Minted", data.card.minted ? new Date(data.card.minted).toLocaleDateString(undefined, { year: "numeric", month: "long", day: "numeric" }) : "—", "verify-minted", "text-zinc-300"]].map(([l, v, tid, cls]) => (
                  <div key={tid}>
                    <div className="text-[10px] uppercase tracking-[0.25em] text-zinc-500 mb-0.5">{l}</div>
                    <div className={cls} data-testid={tid}>{v}</div>
                  </div>
                ))}
              </div>
            </div>

            <div className="mb-10" data-testid="verify-history">
              <h2 className="font-display font-extrabold uppercase tracking-wide text-lime-300 text-lg mb-4">Provenance — Transfer History</h2>
              {data.card.autograph && (
                <div className="border border-yellow-500/50 bg-yellow-500/5 px-4 py-3 mb-3 text-sm" data-testid="verify-autograph-trail">
                  <div className="font-black uppercase tracking-widest text-yellow-300 text-xs mb-1">🖊 Verified Fan Signed Card</div>
                  <div className="text-xs text-zinc-300 space-y-0.5">
                    <div>Signed by <span className="font-bold text-white">{data.card.autograph.pro_name}</span></div>
                    <div>Athlete verified in person {data.card.autograph.verified_at ? new Date(data.card.autograph.verified_at).toLocaleString() : ""} · {data.card.autograph.confidence}% verified</div>
                    <div>Signature baked {data.card.autograph.signed_at ? new Date(data.card.autograph.signed_at).toLocaleString() : ""} — permanently embedded in this copy. Signed once, locked forever.</div>
                  </div>
                </div>
              )}
              {data.transfers.length === 0 ? (
                <div className="border border-dashed border-zinc-800 p-6 text-zinc-500 text-sm" data-testid="verify-no-transfers">
                  No transfers on record — this card has stayed with its original owner since minting.
                </div>
              ) : (
                <div className="border border-zinc-800 divide-y divide-zinc-800/70">
                  {data.transfers.map((t, i) => (
                    <div key={i} className="flex flex-wrap items-center gap-2 px-4 py-3 text-sm" data-testid={`verify-transfer-${i}`}>
                      <span className="font-mono text-[10px] text-zinc-600 w-6">#{i + 1}</span>
                      <span className="font-semibold">{t.from_name}</span>
                      <ArrowRight className="w-3.5 h-3.5 text-lime-400" />
                      <span className="font-semibold">{t.to_name}</span>
                      <span className="ml-auto text-xs text-zinc-500">{t.created_at ? new Date(t.created_at).toLocaleString() : ""}</span>
                    </div>
                  ))}
                </div>
              )}
            </div>

            <div className="border border-zinc-800 bg-white/[0.02] p-6 flex flex-col sm:flex-row items-center gap-6" data-testid="verify-qr-block">
              <div className="bg-white p-3 shrink-0">
                <QRCodeSVG value={verifyUrl} size={130} data-testid="verify-qr" />
              </div>
              <div>
                <div className="font-display font-bold uppercase mb-1">Scan to verify</div>
                <p className="text-xs text-zinc-500 leading-relaxed">
                  This QR code links straight to this registry page. Print it, share it, or attach it to a physical
                  copy — anyone who scans it sees the live ownership record, so fakes can't hold up.
                </p>
              </div>
            </div>
          </>
        )}

        <div className="border border-zinc-800 bg-white/[0.02] p-6 mt-10" data-testid="verify-scan-block">
          <div className="flex items-center gap-2 mb-1">
            <ScanSearch className="w-4 h-4 text-lime-300" />
            <span className="font-display font-bold uppercase">Check a card image</span>
          </div>
          <p className="text-xs text-zinc-500 mb-4 leading-relaxed">
            Every genuine Bullpen card copy carries an invisible digital watermark embedded in its pixels —
            in addition to the printed serial, micro-text and security pattern. Upload the exact card image
            file (PNG) and we'll read the hidden signature to confirm it's real.
          </p>
          <input ref={fileRef} type="file" accept="image/*" className="hidden" data-testid="verify-scan-input"
            onChange={(e) => scanImage(e.target.files?.[0])} />
          <button data-testid="verify-scan-btn" onClick={() => fileRef.current?.click()} disabled={scanning}
            className="inline-flex items-center gap-2 border border-lime-400/60 text-lime-300 text-[10px] font-black uppercase tracking-widest px-5 py-2.5 hover:bg-lime-400/10 transition-colors disabled:opacity-50">
            {scanning ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Upload className="w-3.5 h-3.5" />}
            Upload image to scan
          </button>
          {scan && (
            scan.found && scan.valid ? (
              <div className="mt-4 border border-emerald-500/50 bg-emerald-500/10 px-4 py-3 text-sm" data-testid="scan-result-valid">
                <span className="font-black text-emerald-400 uppercase">Genuine watermark found</span> — serial{" "}
                <Link to={`/card/verify/${scan.serial}`} className="font-mono font-bold text-lime-300 underline">{scan.serial}</Link>
                {scan.card_name ? <> · "{scan.card_name}"</> : null}
                {scan.owner_name ? <> · registered to <span className="font-semibold">{scan.owner_name}</span></> : null}
              </div>
            ) : scan.found ? (
              <div className="mt-4 border border-red-500/50 bg-red-500/10 px-4 py-3 text-sm" data-testid="scan-result-invalid">
                <span className="font-black text-red-400 uppercase">Warning</span> — a watermark was found but its
                signature does not match the registry. This image has likely been tampered with.
              </div>
            ) : (
              <div className="mt-4 border border-amber-500/50 bg-amber-500/10 px-4 py-3 text-sm" data-testid="scan-result-none">
                <span className="font-black text-amber-400 uppercase">No watermark detected</span> — this image does not
                carry a Bullpen security signature. It may be a screenshot, a re-compressed copy, or a fake. Always
                verify by serial number above.
              </div>
            )
          )}
        </div>
      </div>
    </div>
  );
}
